
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { db } from '../firebase';
import { 
    collection, query, orderBy, getDocs, doc, updateDoc, 
    getDoc, where, limit, deleteDoc, addDoc, serverTimestamp, setDoc, onSnapshot
} from 'firebase/firestore';
import { Card, Button, Badge, Input } from '../components/ui';
import { InvoiceRequest, ChatMessage, UserProfile, HomeBlock } from '../types';
import { COURSES } from '../data/courses';
import { moderateBatch, BatchModerationResult } from '../utils/moderation';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'invoices' | 'members' | 'landing' | 'forum' | 'speakers' | 'communication' | 'utilities' | 'settings'>('members');
  const [invoices, setInvoices] = useState<InvoiceRequest[]>([]);
  const [loading, setLoading] = useState(true);

  // Home Blocks State
  const [homeBlocks, setHomeBlocks] = useState<HomeBlock[]>([]);
  const [editingBlock, setEditingBlock] = useState<Partial<HomeBlock> | null>(null);
  const [isSavingBlock, setIsSavingBlock] = useState(false);

  // Members Directory State
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [activeMembersUidMap, setActiveMembersUidMap] = useState<Record<string, boolean>>({});

  // Speaker Invite State
  const [speakerEmail, setSpeakerEmail] = useState('');
  const [speakerName, setSpeakerName] = useState('');
  const [sessionTitle, setSessionTitle] = useState('Live Legal Support Q&A');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteStatus, setInviteStatus] = useState('');

  // Schedule State
  const [schedName, setSchedName] = useState('');
  const [schedTime, setSchedTime] = useState('');
  const [schedDesc, setSchedDesc] = useState('');
  const [isUpdatingSched, setIsUpdatingSched] = useState(false);

  // Forum Moderation State
  const [selectedCourseId, setSelectedCourseId] = useState(COURSES[0].id);
  const [forumMessages, setForumMessages] = useState<ChatMessage[]>([]);
  const [isSweeping, setIsSweeping] = useState(false);
  const [sweepResults, setSweepResults] = useState<BatchModerationResult | null>(null);

  // Broadcast State
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState('');

  // Global Settings State
  const [stripeTestMode, setStripeTestMode] = useState(false);

  // Utility States
  const [fixUserEmail, setFixUserEmail] = useState('');
  const [fixStatus, setFixStatus] = useState('');
  const [isClearingLogs, setIsClearingLogs] = useState(false);
  const [clearLogsStatus, setClearLogsStatus] = useState('');

  const handleAdminConnect = async (memberUid: string) => {
    if (!userProfile) return;
    setIsConnecting(memberUid);
    try {
      const q = query(
        collection(db, 'connections'),
        where('users', 'array-contains', userProfile.uid)
      );
      const snap = await getDocs(q);
      const existingConn = snap.docs.find(d => d.data().users.includes(memberUid));

      if (existingConn) {
        if (existingConn.data().status !== 'accepted') {
          await updateDoc(existingConn.ref, { status: 'accepted', updatedAt: serverTimestamp() });
        }
        navigate('/messages');
      } else {
        const connectionData = {
          users: [userProfile.uid, memberUid],
          requesterUid: userProfile.uid,
          receiverUid: memberUid,
          status: 'accepted',
          updatedAt: serverTimestamp()
        };
        await addDoc(collection(db, 'connections'), connectionData);
        navigate('/messages');
      }
    } catch (err) {
      console.error("Failed to connect", err);
      alert("Failed to connect to member. See console.");
    } finally {
      setIsConnecting(null);
    }
  };

  const handleUpdateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingSched(true);
    try {
        await setDoc(doc(db, 'config', 'broadcast_schedule'), {
            nextSpeaker: schedName,
            scheduledAt: schedTime,
            description: schedDesc,
            updatedAt: serverTimestamp()
        });
        alert("Broadcast schedule updated!");
    } catch (err) {
        alert("Failed to update schedule");
    } finally {
        setIsUpdatingSched(false);
    }
  };

  const handleInviteSpeaker = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!speakerEmail || !speakerName) return;
    setIsInviting(true);
    setInviteStatus('Generating secure link...');

    try {
        const inviteToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        const inviteRef = doc(collection(db, 'speaker_invites'));
        
        await setDoc(inviteRef, {
            email: speakerEmail,
            name: speakerName,
            token: inviteToken,
            title: sessionTitle,
            status: 'pending',
            createdAt: serverTimestamp(),
            expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000) // 48 hours
        });

        const inviteLink = `${window.location.origin}/#/broadcast?invite=${inviteToken}`;

        await addDoc(collection(db, 'mail'), {
            to: [speakerEmail],
            message: {
                subject: `Invitation to Speak: ${sessionTitle}`,
                text: `Hi ${speakerName}, you've been invited to host a Live Support session on Dads Matter Hub. Access your speaker portal here: ${inviteLink}`,
                html: `
                    <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; padding: 40px; background: #f8fafc; border-radius: 24px;">
                        <h2 style="color: #1e293b; margin-bottom: 20px;">Guest Speaker Invitation</h2>
                        <p style="color: #475569; font-size: 16px; line-height: 1.6;">Hello <strong>${speakerName}</strong>,</p>
                        <p style="color: #475569; font-size: 16px; line-height: 1.6;">You have been invited by the Dads Matter Admin Team to host a live video broadcast for our community.</p>
                        
                        <div style="background: white; padding: 24px; border-radius: 16px; margin: 30px 0; border: 1px solid #e2e8f0;">
                            <p style="margin: 0; font-size: 12px; color: #94a3b8; text-transform: uppercase; font-weight: bold;">Session Topic</p>
                            <p style="margin: 5px 0 0 0; font-size: 18px; color: #4f46e5; font-weight: bold;">${sessionTitle}</p>
                        </div>

                        <p style="color: #475569; font-size: 14px;">This link provides <strong>one-time host access</strong> to our broadcast stage. You do not need an account to use this link.</p>
                        
                        <div style="text-align: center; margin: 40px 0;">
                            <a href="${inviteLink}" style="background-color: #4f46e5; color: #ffffff; padding: 16px 32px; text-decoration: none; border-radius: 12px; font-weight: bold; font-size: 16px; display: inline-block; box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.3);">Access Speaker Portal</a>
                        </div>

                        <p style="color: #94a3b8; font-size: 12px; text-align: center;">This link is unique to you and should not be shared. It expires in 48 hours.</p>
                    </div>
                `
            }
        });

        setInviteStatus('✅ Invitation sent to ' + speakerEmail);
        setSpeakerEmail('');
        setSpeakerName('');
    } catch (err: any) {
        setInviteStatus('❌ Failed: ' + err.message);
    } finally {
        setIsInviting(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'invoice_requests'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data: InvoiceRequest[] = [];
      snapshot.forEach(doc => {
          data.push({ id: doc.id, ...doc.data() } as InvoiceRequest);
      });
      setInvoices(data);
    } catch (e) {
      console.error("Error fetching invoices", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
      setIsMembersLoading(true);
      try {
          const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
          const snapshot = await getDocs(q);
          const data: UserProfile[] = [];
          snapshot.forEach(doc => {
              data.push({ ...doc.data() } as UserProfile);
          });
          setMembers(data);
      } catch (e) {
          console.error("Error fetching members", e);
      } finally {
          setIsMembersLoading(false);
      }
  };

  const fetchHomeBlocks = async () => {
      try {
          const q = query(collection(db, 'home_blocks'), orderBy('order', 'asc'));
          const snapshot = await getDocs(q);
          const blocks: HomeBlock[] = [];
          snapshot.forEach(doc => {
              blocks.push({ id: doc.id, ...doc.data() } as HomeBlock);
          });
          setHomeBlocks(blocks);
      } catch (e) {
          console.error("Error fetching home blocks", e);
      }
  };

  const handleSaveBlock = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!editingBlock) return;
      
      setIsSavingBlock(true);
      try {
          if (editingBlock.id) {
              // Update existing
              await updateDoc(doc(db, 'home_blocks', editingBlock.id), {
                  ...editingBlock,
                  updatedAt: serverTimestamp()
              });
          } else {
              // Create new
              await addDoc(collection(db, 'home_blocks'), {
                  ...editingBlock,
                  order: homeBlocks.length, // auto append to end
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp()
              });
          }
          setEditingBlock(null);
          await fetchHomeBlocks();
      } catch (e) {
          console.error("Error saving block", e);
          alert("Failed to save block");
      } finally {
          setIsSavingBlock(false);
      }
  };

  const handleDeleteBlock = async (id: string) => {
      if(!confirm("Are you sure you want to delete this block?")) return;
      try {
          await deleteDoc(doc(db, 'home_blocks', id));
          await fetchHomeBlocks();
      } catch(e) {
          alert("Failed to delete block");
      }
  };

  const handleMoveBlock = async (id: string, direction: 'up' | 'down') => {
      const index = homeBlocks.findIndex(b => b.id === id);
      if (index === -1) return;
      if (direction === 'up' && index === 0) return;
      if (direction === 'down' && index === homeBlocks.length - 1) return;

      const newBlocks = [...homeBlocks];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      // Swap positions
      const temp = newBlocks[index];
      newBlocks[index] = newBlocks[targetIndex];
      newBlocks[targetIndex] = temp;

      // Update local state for immediate feedback
      setHomeBlocks(newBlocks);

      // Persist new ordering
      try {
          const batchPromises = newBlocks.map((block, idx) => {
              return updateDoc(doc(db, 'home_blocks', block.id), { order: idx });
          });
          await Promise.all(batchPromises);
      } catch (e) {
          console.error("Failed to persist reordering", e);
          fetchHomeBlocks(); // revert on fail
      }
  };

  const fetchForumMessages = async (courseId: string) => {
      const q = query(
          collection(db, 'forum_messages'),
          where('courseId', '==', courseId),
          orderBy('createdAt', 'desc'),
          limit(50)
      );
      const snapshot = await getDocs(q);
      const msgs: ChatMessage[] = [];
      snapshot.forEach(doc => {
          msgs.push({ id: doc.id, ...doc.data() } as ChatMessage);
      });
      setForumMessages(msgs);
      setSweepResults(null);
  };

  const handleRunSweep = async () => {
      if (forumMessages.length === 0) return;
      setIsSweeping(true);
      try {
          const result = await moderateBatch(forumMessages);
          setSweepResults(result);
      } catch (err) {
          console.error("Safety sweep failed:", err);
      } finally {
          setIsSweeping(false);
      }
  };

  const handleSendBroadcast = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!broadcastSubject || !broadcastBody) return;
      if (!confirm("Are you sure you want to send this email to ALL registered users?")) return;

      setIsBroadcasting(true);
      setBroadcastStatus('Fetching users...');

      try {
          const userSnap = await getDocs(collection(db, 'users'));
          const emails: string[] = [];
          userSnap.forEach(doc => {
              const data = doc.data() as UserProfile;
              if (data.email) emails.push(data.email);
          });

          setBroadcastStatus(`Sending to ${emails.length} users...`);

          for (const email of emails) {
              await addDoc(collection(db, 'mail'), {
                  to: email,
                  message: {
                      subject: broadcastSubject,
                      text: broadcastBody,
                      html: `<div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #eee; border-radius: 10px;">
                                <div style="font-size: 16px; line-height: 1.6; color: #333;">
                                    ${broadcastBody.replace(/\n/g, '<br>')}
                                </div>
                                <hr style="margin: 30px 0; border: none; border-top: 1px solid #eee;" />
                                <p style="font-size: 12px; color: #999; text-align: center;">You are receiving this as a member of the Dads Matter Training Hub.</p>
                             </div>`
                  }
              });
          }

          setBroadcastStatus('✅ Broadcast successful!');
          setBroadcastSubject('');
          setBroadcastBody('');
      } catch (err: any) {
          setBroadcastStatus(`❌ Error: ${err.message}`);
      } finally {
          setIsBroadcasting(false);
      }
  };

  const handleDeleteMessage = async (msgId: string) => {
      if (!confirm("Permanently delete this message?")) return;
      try {
          await deleteDoc(doc(db, 'forum_messages', msgId));
          setForumMessages(prev => prev.filter(m => m.id !== msgId));
      } catch (e) {
          alert("Error deleting message");
      }
  };

  const fetchSettings = async () => {
      try {
          const docRef = doc(db, 'config', 'app_settings');
          const snap = await getDoc(docRef);
          if (snap.exists()) {
              setStripeTestMode(snap.data().stripeTestMode || false);
          }

          const schedRef = doc(db, 'config', 'broadcast_schedule');
          const schedSnap = await getDoc(schedRef);
          if (schedSnap.exists()) {
              setSchedName(schedSnap.data().nextSpeaker || '');
              setSchedTime(schedSnap.data().scheduledAt || '');
              setSchedDesc(schedSnap.data().description || '');
          }
      } catch (e) {
          console.error("Error fetching settings", e);
      }
  };

  useEffect(() => {
    fetchMembers();
    fetchInvoices();
    fetchSettings();
    fetchHomeBlocks();
  }, []);

  useEffect(() => {
      if (activeTab === 'members') {
          const q = query(collection(db, 'presence'));
          const unsubscribe = onSnapshot(q, (snapshot) => {
              const activeUids: Record<string, boolean> = {};
              const now = Date.now();
              snapshot.forEach(doc => {
                  const data = doc.data();
                  const lastActiveMs = data.lastActive?.toMillis ? data.lastActive.toMillis() : now;
                  if (now - lastActiveMs <= 150000) { // 2.5 minutes online threshold
                      activeUids[data.uid] = true;
                  }
              });
              setActiveMembersUidMap(activeUids);
          });
          return () => unsubscribe();
      }
  }, [activeTab]);

  useEffect(() => {
      if (activeTab === 'forum') {
          fetchForumMessages(selectedCourseId);
      }
  }, [activeTab, selectedCourseId]);

  const handleMarkPaid = async (inv: InvoiceRequest) => {
      if(!confirm(`Mark invoice ${inv.invoiceNumber} as PAID and grant access?`)) return;
      try {
          await updateDoc(doc(db, 'invoice_requests', inv.id), { status: 'paid' });
          if (inv.schoolId) {
             await updateDoc(doc(db, 'schools', inv.schoolId), {
                 subscriptionStatus: 'active',
                 isPaid: true
             });
          }
          fetchInvoices();
          alert("Invoice marked as paid.");
      } catch(e) { console.error(e); }
  };

  const handleForceActivateByEmail = async (email: string) => {
    if (!email) return;
    setFixStatus('Activating...');
    try {
        const q = query(collection(db, 'users'), where('email', '==', email));
        const snap = await getDocs(q);
        if (snap.empty) { setFixStatus('❌ Email not found in records'); return; }
        
        const userDoc = snap.docs[0];
        await updateDoc(doc(db, 'users', userDoc.id), { 
            isPaid: true, 
            subscriptionStatus: 'active',
            planTier: 'tier_1',
            lastUpdated: serverTimestamp()
        });
        setFixStatus(`✅ Successfully Activated ${email}`);
        setFixUserEmail('');
    } catch (e: any) { setFixStatus('❌ Database error during activation'); }
  };

  const handleClearLegacyLogs = async () => {
    if (!window.confirm("Are you sure? This will remove all legacy logs (those without a 'diagnosticState' or 'words' array) from all user profiles. This cannot be undone.")) return;
    setIsClearingLogs(true);
    setClearLogsStatus('Scanning all users...');
    try {
        const snap = await getDocs(collection(db, 'users'));
        let usersUpdated = 0;
        let checksDeleted = 0;

        for (const userDoc of snap.docs) {
            const data = userDoc.data() as UserProfile;
            if (data.thermalCheckins && data.thermalCheckins.length > 0) {
                // Keep only checks that have Phase 1/Phase 4 System Check properties
                const validChecks = data.thermalCheckins.filter(check => 
                    check.diagnosticState !== undefined || 
                    (check.words && Array.isArray(check.words) && check.words.length > 0) ||
                    check.weather !== undefined ||
                    (check.points && check.points.length > 0)
                );
                
                if (validChecks.length !== data.thermalCheckins.length) {
                    checksDeleted += (data.thermalCheckins.length - validChecks.length);
                    usersUpdated++;
                    await updateDoc(doc(db, 'users', userDoc.id), { thermalCheckins: validChecks });
                }
            }
        }
        
        setClearLogsStatus(`✅ Cleanup complete: Removed ${checksDeleted} legacy log(s) across ${usersUpdated} user(s).`);
    } catch(err: any) {
        console.error(err);
        setClearLogsStatus(`❌ Error during cleanup: ${err.message}`);
    } finally {
        setIsClearingLogs(false);
    }
  };

  const handlePromoteToBuddy = async (user: UserProfile) => {
      const isAlreadyBuddy = user.isBuddy;
      if (isAlreadyBuddy) {
          if(!confirm(`Revoke Buddy status for ${user.displayName}?`)) return;
          try {
              await updateDoc(doc(db, 'users', user.uid), {
                  isBuddy: false,
                  'buddyProfile.status': 'inactive'
              });
              fetchMembers();
              alert("Buddy status revoked.");
          } catch(e) { console.error(e); }
      } else {
          if(!confirm(`Send Buddy invite to ${user.displayName}?`)) return;
          try {
              await updateDoc(doc(db, 'users', user.uid), {
                  isBuddy: true,
                  buddyProfile: {
                      status: 'pending',
                      topics: [],
                      bio: '',
                      capacity: 5
                  }
              });

              if (user.email) {
                  const inviteLink = `${window.location.origin}/#/settings`;
                  await addDoc(collection(db, 'mail'), {
                      to: user.email,
                      message: {
                          subject: "You've been invited to be a Dads Matter Buddy!",
                          text: `Hi ${user.displayName || 'there'},\n\nYou have been invited to become a Dads Matter Buddy! As a Buddy, your lived experience can help support other dads currently navigating the family courts, alienation, or other challenges.\n\nAs a Buddy, you CAN:\n- Share your lived experience and what worked for you.\n- Offer emotional support and a listening ear.\n- Signpost to professional resources.\n\nAs a Buddy, you CANNOT:\n- Give direct legal advice or act as a solicitor.\n- Offer clinical or medical advice.\n- Guarantee specific outcomes in their cases.\n\nPlease click the link below to go to your Settings page and set up your Buddy Profile (areas of expertise, short bio, and capacity):\n\n${inviteLink}\n\nThank you for your support!\nThe Dads Matter Team`,
                          html: `<div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #eee; border-radius: 10px;">
                                    <h2 style="color: #4f46e5; text-align: center;">You've been invited to be a Dads Matter Buddy! 🤝</h2>
                                    <p style="font-size: 16px; color: #333; line-height: 1.6;">Hi ${user.displayName || 'there'},</p>
                                    <p style="font-size: 16px; color: #333; line-height: 1.6;">We believe your lived experience could be invaluable in supporting other dads who are currently navigating difficult times.</p>
                                    
                                    <div style="background-color: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0; border: 1px solid #e2e8f0;">
                                        <h3 style="color: #0f172a; margin-top: 0; font-size: 16px;">What it means to be a Buddy:</h3>
                                        <p style="font-size: 14px; color: #059669; font-weight: bold; margin-bottom: 5px;">✅ You CAN:</p>
                                        <ul style="font-size: 14px; color: #333; margin-top: 0;">
                                            <li>Share your lived experience and what worked for you.</li>
                                            <li>Offer emotional support and a listening ear.</li>
                                            <li>Signpost to professional resources.</li>
                                        </ul>
                                        <p style="font-size: 14px; color: #e11d48; font-weight: bold; margin-bottom: 5px; margin-top: 15px;">❌ You CANNOT:</p>
                                        <ul style="font-size: 14px; color: #333; margin-top: 0;">
                                            <li>Give direct legal advice or act as a solicitor.</li>
                                            <li>Offer clinical or medical advice.</li>
                                            <li>Guarantee specific outcomes in their cases.</li>
                                        </ul>
                                    </div>

                                    <p style="font-size: 16px; color: #333; line-height: 1.6;">To get started, please click the button below to go to your <strong>Settings page</strong> and complete your Buddy Profile. You'll need to select your areas of expertise, write a short bio, and set your mentorship capacity.</p>
                                    <div style="text-align: center; margin: 30px 0;">
                                        <a href="${inviteLink}" style="background-color: #f59e0b; color: #ffffff; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; display: inline-block;">Go to Settings & Set Up Profile</a>
                                    </div>
                                    <p style="font-size: 16px; color: #333; line-height: 1.6;">Thank you for your support and willingness to help others.</p>
                                    <hr style="margin: 30px 0; border: none; border-top: 1px solid #eee;" />
                                    <p style="font-size: 12px; color: #999; text-align: center;">The Dads Matter Team</p>
                                 </div>`
                      }
                  });
              }

              fetchMembers();
              alert("Buddy invite sent!");
          } catch(e) { console.error(e); }
      }
  };

  const handleToggleSandboxAccess = async (user: UserProfile) => {
      const newState = !user.sandboxAccess;
      if(!confirm(`${newState ? 'Grant' : 'Revoke'} Sandbox Access for ${user.displayName}?`)) return;
      try {
          await updateDoc(doc(db, 'users', user.uid), { sandboxAccess: newState });
          fetchMembers();
      } catch(e) { console.error(e); }
  };

  const handleTogglePaidStatus = async (user: UserProfile) => {
      const isCurrentlyPaid = user.isPaid || user.subscriptionStatus === 'active';
      const newState = !isCurrentlyPaid;
      if(!confirm(`Change Paid/Pro Status to ${newState ? 'ON' : 'OFF'} for ${user.displayName}?`)) return;
      try {
          await updateDoc(doc(db, 'users', user.uid), { 
              isPaid: newState,
              subscriptionStatus: newState ? 'active' : 'inactive'
          });
          fetchMembers();
      } catch(e) { console.error(e); }
  };

  const filteredMembers = members.filter(m => {
      const q = memberSearchQuery.toLowerCase();
      return (m.displayName || '').toLowerCase().includes(q) || 
             (m.email || '').toLowerCase().includes(q);
  }).sort((a, b) => {
      // Put needsSupport true at the top
      if (a.needsSupport && !b.needsSupport) return -1;
      if (!a.needsSupport && b.needsSupport) return 1;
      
      // Then sort alphabetically by name
      const nameA = a.displayName || '';
      const nameB = b.displayName || '';
      return nameA.localeCompare(nameB);
  });

  const getZoneWord = (val: number) => {
      if (val <= -4) return "Checked out";
      if (val <= -1) return "Distant";
      if (val === 0) return "Present";
      if (val <= 3) return "Stressed";
      return "Boiling";
  };
  
  const getDotColor = (val: number | null) => {
      if (val === null) return '#e2e8f0'; // slate-200 for empty
      if (val < 0) return '#4f46e5'; // indigo-600
      if (val > 0) return '#e11d48'; // rose-600
      return '#059669'; // emerald-600
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="flex justify-between items-center">
            <h2 className="text-3xl font-heading font-bold text-slate-900">Admin Portal</h2>
            <Badge color="red">SECURE ACCESS</Badge>
        </div>
        <div className="flex flex-wrap gap-4 mt-4 pb-2">
             <button onClick={() => setActiveTab('members')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${activeTab === 'members' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}>Members Directory</button>
             <button onClick={() => setActiveTab('invoices')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${activeTab === 'invoices' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}>Invoices & Billing</button>
             <button onClick={() => setActiveTab('landing')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${activeTab === 'landing' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}>Landing Editor <span className="text-[10px] bg-indigo-500 px-1.5 py-0.5 rounded ml-1 text-white">NEW</span></button>
             <button onClick={() => setActiveTab('utilities')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${activeTab === 'utilities' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}>Account Recovery</button>
             <button onClick={() => setActiveTab('forum')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${activeTab === 'forum' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}>Forum Audit</button>
             <button onClick={() => setActiveTab('speakers')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${activeTab === 'speakers' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}>Speaker & Schedule</button>
             <button onClick={() => setActiveTab('communication')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${activeTab === 'communication' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}>Communication</button>
             <button onClick={() => setActiveTab('settings')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${activeTab === 'settings' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200'}`}>Settings</button>
        </div>
      </div>

      {activeTab === 'utilities' && (
          <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
              <Card className="p-8 border-indigo-200 bg-indigo-50/50 shadow-xl">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-indigo-900">Force Activate User</h3>
                    <p className="text-sm text-indigo-700">If a user has paid but is stuck on the subscription page, enter their email below to grant immediate access.</p>
                  </div>
                  <div className="flex gap-4 items-end">
                      <div className="flex-1">
                        <Input 
                            label="User Email Address" 
                            value={fixUserEmail} 
                            onChange={e => setFixUserEmail(e.target.value)} 
                            placeholder="user@example.com"
                            className="bg-white mb-0" 
                        />
                      </div>
                      <div className="mb-5">
                        <Button 
                            onClick={() => handleForceActivateByEmail(fixUserEmail)} 
                            disabled={!fixUserEmail}
                            className="!px-8 shadow-indigo-200"
                        >
                            Grant Full Access
                        </Button>
                      </div>
                  </div>
                  {fixStatus && (
                    <div className={`mt-4 p-4 rounded-xl text-sm font-bold animate-slide-up ${fixStatus.includes('✅') ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-white border border-slate-200'}`}>
                        {fixStatus}
                    </div>
                  )}
              </Card>

              <Card className="p-8 border-slate-200 bg-white">
                 <h3 className="text-lg font-bold mb-2">Troubleshooting Help</h3>
                 <p className="text-slate-500 text-xs text-balance">Users may be stuck if they close the Stripe tab before the redirect happens. Activating them here updates their Firestore record instantly. They just need to refresh their Dashboard.</p>
              </Card>

              <Card className="p-8 border-rose-200 bg-rose-50/50 shadow-xl">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-rose-900">Database Cleanup</h3>
                    <p className="text-sm text-rose-700">Remove obsolete records and clear out legacy variables.</p>
                  </div>
                  <div className="flex gap-4 items-end">
                      <div className="flex-1">
                        <p className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2">Legacy Mood Map Logs</p>
                        <p className="text-sm text-rose-800 font-medium">This will destroy any mood logs that do not conform to the new 4-Phase System Check model. This cannot be undone.</p>
                      </div>
                      <div className="mb-1 shrink-0">
                        <Button 
                            onClick={handleClearLegacyLogs} 
                            disabled={isClearingLogs}
                            className="!px-8 bg-rose-600 hover:bg-rose-700 border-rose-500 shadow-rose-200"
                        >
                            {isClearingLogs ? 'Scanning...' : 'Clear Legacy Logs'}
                        </Button>
                      </div>
                  </div>
                  {clearLogsStatus && (
                    <div className={`mt-4 p-4 rounded-xl text-sm font-bold animate-slide-up ${clearLogsStatus.includes('✅') ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-white border border-rose-200 text-rose-800'}`}>
                        {clearLogsStatus}
                    </div>
                  )}
              </Card>
          </div>
      )}

      {activeTab === 'members' && (
          <Card className="overflow-hidden animate-fade-in border-0 shadow-xl">
            <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Members Directory</h3>
                <p className="text-sm text-slate-500">Total registered users: {members.length}</p>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <Input 
                    label="Find Member"
                    placeholder="Search name or email..." 
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="mb-0 bg-slate-50 w-full md:w-64"
                />
                <Button onClick={() => fetchMembers()} variant="outline" className="text-xs shrink-0 !py-2.5 mt-6">Refresh</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              {isMembersLoading ? (
                  <div className="p-10 text-center text-slate-400 font-bold animate-pulse">Loading members...</div>
              ) : (
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 border-y border-slate-200 text-xs uppercase font-bold text-slate-500">
                    <tr>
                      <th className="px-6 py-5">Name</th>
                      <th className="px-6 py-5">State</th>
                      <th className="px-6 py-5">Email Address</th>
                      <th className="px-6 py-5">Role</th>
                      <th className="px-6 py-5">Joined</th>
                      <th className="px-6 py-5 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredMembers.length === 0 ? (
                      <tr><td colSpan={6} className="px-6 py-20 text-center text-slate-400">No members found.</td></tr>
                    ) : filteredMembers.map((user, idx) => {
                      const latestCheckin = user.thermalCheckins?.length 
                          ? [...user.thermalCheckins].sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
                          : null;
                      
                      return (
                      <tr key={user.uid || idx} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                                {activeMembersUidMap[user.uid] && (
                                    <div className="w-2.5 h-2.5 shrink-0 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse" title="Online Now"></div>
                                )}
                                <div className="font-bold text-slate-800">{user.displayName || 'Unnamed User'}</div>
                                {user.needsSupport && (
                                    <span className="bg-rose-100 text-rose-700 text-[10px] font-bold px-2 py-0.5 rounded-full outline outline-1 outline-rose-200 animate-pulse whitespace-nowrap" title="Waitlisting for Buddy Support">Needs Support 🆘</span>
                                )}
                            </div>
                            {user.nickname && <div className="text-xs text-slate-400 pl-4">{user.nickname}</div>}
                        </td>
                        <td className="px-6 py-4">
                            {latestCheckin ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getDotColor(latestCheckin.value) }} />
                                    <span className="text-xs font-bold text-slate-700">{getZoneWord(latestCheckin.value)}</span>
                                    <span className="text-[10px] text-slate-400">({latestCheckin.value > 0 ? '+'+latestCheckin.value : latestCheckin.value})</span>
                                </div>
                            ) : (
                                <span className="text-xs text-slate-400 italic">No check-ins</span>
                            )}
                        </td>
                        <td className="px-6 py-4 text-slate-600">{user.email || 'No email provided'}</td>
                        <td className="px-6 py-4">
                            <Badge color={user.role === 'admin' ? 'red' : user.role === 'manager' ? 'yellow' : 'blue'}>
                                {user.role || 'parent'}
                            </Badge>
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500">
                            {user.createdAt?.toDate ? new Date(user.createdAt.toDate()).toLocaleDateString('en-GB') : '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                            <div className="flex flex-col items-end gap-2">
                                {user.needsSupport && (
                                    <Button 
                                        onClick={async () => {
                                            if (!window.confirm("Mark support as fulfilled for this user?")) return;
                                            try {
                                                await updateDoc(doc(db, 'users', user.uid), { needsSupport: false });
                                                fetchMembers();
                                            } catch (err) { console.error("Failed to update support status", err); }
                                        }} 
                                        className="!py-1.5 !px-3 text-xs bg-rose-50 text-rose-700 hover:bg-rose-100 border-none flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity w-full justify-center lg:w-32"
                                    >
                                        ✓ Resolve
                                    </Button>
                                )}
                                {userProfile?.uid !== user.uid && (
                                    <Button 
                                        onClick={() => handleAdminConnect(user.uid)} 
                                        disabled={isConnecting === user.uid}
                                        className="!py-1.5 !px-3 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-none flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity w-full justify-center lg:w-32"
                                    >
                                        💬 {isConnecting === user.uid ? 'Connecting...' : 'Message'}
                                    </Button>
                                )}
                                <Button 
                                    onClick={() => handlePromoteToBuddy(user)}
                                    variant="outline"
                                    className={`!py-1.5 !px-3 text-xs border flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity w-full justify-center lg:w-32 ${user.isBuddy ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                                >
                                    🤝 {user.isBuddy ? 'Revoke Buddy' : 'Make Buddy'}
                                </Button>
                                <Button 
                                    onClick={() => handleToggleSandboxAccess(user)}
                                    variant="outline"
                                    className={`!py-1.5 !px-3 text-xs border flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity w-full justify-center lg:w-32 ${user.sandboxAccess ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                                >
                                    🧪 {user.sandboxAccess ? 'Sandbox: ON' : 'Sandbox: OFF'}
                                </Button>
                                <Button 
                                    onClick={() => handleTogglePaidStatus(user)}
                                    variant="outline"
                                    className={`!py-1.5 !px-3 text-xs border flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity w-full justify-center lg:w-32 ${user.isPaid || user.subscriptionStatus === 'active' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                                >
                                    ⭐ {(user.isPaid || user.subscriptionStatus === 'active') ? 'Pro: ON' : 'Pro: OFF'}
                                </Button>
                            </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
      )}

      {activeTab === 'landing' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-fade-in">
              <div className="lg:col-span-8 space-y-4">
                  <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-slate-900">Active Blocks</h3>
                      <p className="text-sm text-slate-500">Order determines layout on the Home screen.</p>
                  </div>
                  
                  {homeBlocks.length === 0 ? (
                      <Card className="p-8 text-center text-slate-400">No custom blocks currently active.</Card>
                  ) : (
                      <div className="space-y-3">
                          {homeBlocks.map((block, index) => (
                              <Card key={block.id} className="p-4 flex items-center justify-between group">
                                  <div className="flex items-center gap-4 flex-1">
                                      <div className="flex flex-col gap-1 items-center justify-center text-slate-300 w-8">
                                          <button onClick={() => handleMoveBlock(block.id, 'up')} disabled={index === 0} className="hover:text-indigo-600 disabled:opacity-30">▲</button>
                                          <span className="text-[10px] font-mono">{index + 1}</span>
                                          <button onClick={() => handleMoveBlock(block.id, 'down')} disabled={index === homeBlocks.length - 1} className="hover:text-indigo-600 disabled:opacity-30">▼</button>
                                      </div>
                                      
                                      <div>
                                          <div className="flex items-center gap-2">
                                              <h4 className="font-bold text-slate-800">{block.title}</h4>
                                              {!block.isActive && <Badge color="red">Hidden</Badge>}
                                          </div>
                                          <p className="text-xs text-slate-500">{block.actionUrl} • {(block.visibility || 'all').toUpperCase()}</p>
                                      </div>
                                  </div>
                                  
                                  <div className="flex gap-2">
                                      <Button variant="outline" onClick={() => setEditingBlock(block)} className="!px-3 !py-1 text-xs">Edit</Button>
                                      <Button variant="danger" onClick={() => handleDeleteBlock(block.id)} className="!px-3 !py-1 text-xs">Delete</Button>
                                  </div>
                              </Card>
                          ))}
                      </div>
                  )}
              </div>

              <div className="lg:col-span-4">
                  <Card className="p-6 sticky top-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">{editingBlock?.id ? 'Edit Block' : 'Create New Block'}</h3>
                      <form onSubmit={handleSaveBlock} className="space-y-4">
                          <Input 
                              label="Title" 
                              value={editingBlock?.title || ''} 
                              onChange={e => setEditingBlock(prev => ({...prev, title: e.target.value}))} 
                              required 
                          />
                          <div className="mb-4">
                              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Description</label>
                              <textarea 
                                  value={editingBlock?.description || ''}
                                  onChange={e => setEditingBlock(prev => ({...prev, description: e.target.value}))}
                                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm h-20 resize-none"
                                  required
                              />
                          </div>
                          
                          <div className="grid grid-cols-1 gap-4">
                              <Input 
                                  label="Button Text" 
                                  value={editingBlock?.actionText || ''} 
                                  onChange={e => setEditingBlock(prev => ({...prev, actionText: e.target.value}))} 
                                  placeholder="e.g. View Plan" 
                                  required 
                              />
                          </div>

                          <Input 
                              label="Destination URL" 
                              value={editingBlock?.actionUrl || ''} 
                              onChange={e => setEditingBlock(prev => ({...prev, actionUrl: e.target.value}))} 
                              placeholder="e.g. /subscription" 
                              required 
                          />

                          <div className="grid grid-cols-2 gap-4">
                              <Input 
                                  label="Badge Text (Optional)" 
                                  value={editingBlock?.badgeText || ''} 
                                  onChange={e => setEditingBlock(prev => ({...prev, badgeText: e.target.value}))} 
                                  placeholder="e.g. Upgrade" 
                              />
                              <div className="mb-4">
                                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Badge Color</label>
                                  <select 
                                      value={editingBlock?.badgeColor || 'blue'}
                                      onChange={e => setEditingBlock(prev => ({...prev, badgeColor: e.target.value}))}
                                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                  >
                                      <option value="blue">Blue</option>
                                      <option value="red">Red</option>
                                      <option value="green">Green</option>
                                      <option value="yellow">Yellow</option>
                                  </select>
                              </div>
                          </div>

                          <Input 
                              label="Background Image URL (Optional)" 
                              value={editingBlock?.backgroundImageUrl || ''} 
                              onChange={e => setEditingBlock(prev => ({...prev, backgroundImageUrl: e.target.value}))} 
                              placeholder="https://... (replaces color style)" 
                          />

                          <div className="grid grid-cols-2 gap-4">
                              <div className="mb-4">
                                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Theme Style</label>
                                  <select 
                                      value={editingBlock?.colorStyle || 'indigo'}
                                      onChange={e => setEditingBlock(prev => ({...prev, colorStyle: e.target.value as any}))}
                                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                  >
                                      <option value="indigo">Light Indigo</option>
                                      <option value="emerald">Light Emerald</option>
                                      <option value="amber">Light Amber</option>
                                      <option value="rose">Light Rose</option>
                                      <option value="dark">Dark Mode (Slate)</option>
                                      <option value="gradient_teal">Emerald/Teal Gradient</option>
                                  </select>
                              </div>

                              <div className="mb-4">
                                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Visibility</label>
                                  <select 
                                      value={editingBlock?.visibility || 'all'}
                                      onChange={e => setEditingBlock(prev => ({...prev, visibility: e.target.value as any}))}
                                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                  >
                                      <option value="all">All Users</option>
                                      <option value="free_only">Free Only</option>
                                      <option value="paid_only">Paid / Pro Only</option>
                                  </select>
                              </div>
                          </div>

                          <div className="flex items-center gap-2 mb-4">
                              <input 
                                  type="checkbox" 
                                  id="isActive" 
                                  checked={editingBlock?.isActive ?? true}
                                  onChange={e => setEditingBlock(prev => ({...prev, isActive: e.target.checked}))}
                                  className="w-4 h-4 text-indigo-600 rounded"
                              />
                              <label htmlFor="isActive" className="text-sm font-bold text-slate-700">Block is Active (Visible)</label>
                          </div>

                          <div className="flex gap-2 pt-2">
                              {editingBlock && (
                                  <Button type="button" variant="outline" onClick={() => setEditingBlock(null)} className="flex-1">Cancel</Button>
                              )}
                              <Button type="submit" disabled={isSavingBlock} className="flex-1">
                                  {isSavingBlock ? "Saving..." : "Save Block"}
                              </Button>
                          </div>
                      </form>
                  </Card>
              </div>
          </div>
      )}

      {activeTab === 'invoices' && (
          <Card className="overflow-hidden animate-fade-in border-0 shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-bold text-slate-500">
                  <tr>
                    <th className="px-6 py-5">Status</th>
                    <th className="px-6 py-5">Invoice #</th>
                    <th className="px-6 py-5">Date</th>
                    <th className="px-6 py-5">School / Group</th>
                    <th className="px-6 py-5 text-right">Amount</th>
                    <th className="px-6 py-5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.length === 0 ? (
                    <tr><td colSpan={6} className="px-6 py-20 text-center text-slate-400">No invoice records found.</td></tr>
                  ) : invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4"><Badge color={inv.status === 'paid' ? 'green' : 'yellow'}>{inv.status.toUpperCase()}</Badge></td>
                      <td className="px-6 py-4 font-mono text-xs">{inv.invoiceNumber}</td>
                      <td className="px-6 py-4">{inv.createdAt ? new Date(inv.createdAt.toDate()).toLocaleDateString('en-GB') : '-'}</td>
                      <td className="px-6 py-4 font-bold text-slate-800">{inv.schoolName}</td>
                      <td className="px-6 py-4 text-right font-bold text-slate-900">{inv.price}</td>
                      <td className="px-6 py-4 text-right">
                        {inv.status === 'pending' ? (
                            <button 
                                onClick={() => handleMarkPaid(inv)} 
                                className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-sm"
                            >
                                Activate Access
                            </button>
                        ) : (
                            <span className="text-emerald-600 font-bold text-[10px] uppercase">Active</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
      )}

      {activeTab === 'speakers' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in">
              <Card className="p-8 h-fit">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-900">Invite Guest Speaker</h3>
                    <p className="text-slate-500 text-sm mt-1">One-time host access for live video sessions.</p>
                  </div>
                  <form onSubmit={handleInviteSpeaker} className="space-y-4">
                      <Input label="Speaker Name" value={speakerName} onChange={e => setSpeakerName(e.target.value)} placeholder="e.g. John Smith LL.B" required />
                      <Input label="Email Address" type="email" value={speakerEmail} onChange={e => setSpeakerEmail(e.target.value)} placeholder="speaker@email.com" required />
                      <Input label="Session Title" value={sessionTitle} onChange={e => setSessionTitle(e.target.value)} placeholder="e.g. Navigating Fact Findings" required />
                      
                      {inviteStatus && (
                          <div className={`p-4 rounded-xl text-xs font-bold ${inviteStatus.includes('✅') ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-indigo-50 text-indigo-700'}`}>
                              {inviteStatus}
                          </div>
                      )}
                      
                      <Button type="submit" className="w-full !py-3" disabled={isInviting}>
                          {isInviting ? "Generating Link..." : "Send Invitation Email"}
                      </Button>
                  </form>
              </Card>

              <Card className="p-8 h-fit border-indigo-200 bg-indigo-50/20">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-900">Broadcast Schedule</h3>
                    <p className="text-slate-500 text-sm mt-1">Information displayed in the navigation sidebar for all users.</p>
                  </div>
                  <form onSubmit={handleUpdateSchedule} className="space-y-4">
                      <Input label="Next Speaker Name" value={schedName} onChange={e => setSchedName(e.target.value)} placeholder="e.g. David King (Solicitor)" />
                      <Input label="Scheduled Date/Time" value={schedTime} onChange={e => setSchedTime(e.target.value)} placeholder="e.g. Tomorrow @ 7pm" />
                      
                      <div className="mb-5">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Speaker Description (Visible on Stage Only)</label>
                          <textarea 
                              value={schedDesc}
                              onChange={e => setSchedDesc(e.target.value)}
                              placeholder="Brief bio or session description..."
                              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm h-24 resize-none"
                          />
                      </div>

                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-inner">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Live Preview</p>
                        <p className="text-slate-500 text-xs font-bold">Next: {schedName || 'TBA'}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{schedTime || 'Time to be confirmed'}</p>
                      </div>

                      <Button type="submit" className="w-full !py-3 !bg-slate-900" disabled={isUpdatingSched}>
                          {isUpdatingSched ? "Updating..." : "Update Live Schedule"}
                      </Button>
                  </form>
              </Card>
          </div>
      )}

      {activeTab === 'forum' && (
          <div className="flex flex-col md:flex-row gap-6 h-[70vh]">
              <Card className="w-full md:w-64 overflow-y-auto p-2 bg-slate-50">
                  {COURSES.map(c => (
                      <button 
                        key={c.id} 
                        onClick={() => setSelectedCourseId(c.id)}
                        className={`w-full text-left p-3 rounded-lg text-xs font-bold transition-all mb-1 flex items-center gap-2 ${selectedCourseId === c.id ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-600 hover:bg-white'}`}
                      >
                          <span>{c.icon}</span> <span className="truncate">{c.title}</span>
                      </button>
                  ))}
              </Card>

              <div className="flex-1 flex flex-col gap-4 overflow-hidden">
                  <div className="flex justify-between items-center">
                      <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        {COURSES.find(c => c.id === selectedCourseId)?.icon}
                        {COURSES.find(c => c.id === selectedCourseId)?.title}
                      </h3>
                      <Button onClick={handleRunSweep} disabled={isSweeping || forumMessages.length === 0}>
                          {isSweeping ? 'AI Sweeping...' : 'Run AI Safety Sweep'}
                      </Button>
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-3 pr-2 scrollbar-hide">
                      {forumMessages.map(msg => {
                          const isFlagged = sweepResults?.flaggedIds.includes(msg.id);
                          const aiReason = sweepResults?.explanations[msg.id];
                          return (
                              <Card key={msg.id} className={`p-4 border-l-4 transition-all ${isFlagged ? 'border-rose-500 bg-rose-50 shadow-md' : 'border-slate-200 bg-white'}`}>
                                  <div className="flex justify-between items-start mb-2">
                                      <div className="flex items-center gap-2">
                                          <span className="font-bold text-xs text-slate-900">{msg.userName}</span>
                                          <span className="text-[10px] text-slate-400">
                                              {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleString() : 'Recent'}
                                          </span>
                                      </div>
                                      <button onClick={() => handleDeleteMessage(msg.id)} className="text-slate-300 hover:text-rose-600 text-xs font-bold uppercase tracking-wider">Delete</button>
                                  </div>
                                  <div className="text-sm text-slate-700 leading-relaxed mb-3">
                                      {msg.text}
                                  </div>
                                  {isFlagged && (
                                      <div className="bg-rose-600 text-white p-2 rounded-lg text-[10px] font-bold flex items-center gap-2 animate-slide-up">
                                          <span>🤖 AI WARNING:</span> {aiReason}
                                      </div>
                                  )}
                              </Card>
                          );
                      })}
                  </div>
              </div>
          </div>
      )}

      {activeTab === 'communication' && (
          <div className="max-w-4xl mx-auto animate-fade-in">
              <Card className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-6">Broadcast Email</h3>
                  <form onSubmit={handleSendBroadcast} className="space-y-6">
                      <Input label="Email Subject" value={broadcastSubject} onChange={e => setBroadcastSubject(e.target.value)} required />
                      <textarea className="w-full h-64 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 text-sm" placeholder="Message content..." value={broadcastBody} onChange={e => setBroadcastBody(e.target.value)} required />
                      {broadcastStatus && <div className="p-4 rounded-xl text-sm font-bold bg-indigo-50 text-indigo-700">{broadcastStatus}</div>}
                      <Button type="submit" disabled={isBroadcasting}>{isBroadcasting ? "Broadcasting..." : "Send to All Users"}</Button>
                  </form>
              </Card>
          </div>
      )}

      {activeTab === 'settings' && (
          <div className="max-w-2xl mx-auto">
            <Card className="p-8 text-center">
                <h3 className="text-xl font-bold mb-6">System Settings</h3>
                <Button onClick={async () => {
                    const newValue = !stripeTestMode;
                    setStripeTestMode(newValue);
                    try {
                        await setDoc(doc(db, 'config', 'app_settings'), { stripeTestMode: newValue }, { merge: true });
                    } catch (e) {
                        console.error("Failed to update test mode", e);
                        setStripeTestMode(!newValue);
                        alert("Failed to update test mode in Firestore.");
                    }
                }} variant={stripeTestMode ? 'primary' : 'outline'}>
                    {stripeTestMode ? '🧪 Sandbox Mode: ON' : '✅ Production Mode: ON'}
                </Button>
            </Card>
          </div>
      )}
    </Layout>
  );
};

export default AdminDashboard;
